import { cn } from '@/shared/lib/cn';
import { forwardRef, type ComponentPropsWithoutRef } from 'react';
import { Button } from './button';

type PageItem =
  | { type: 'page'; page: number }
  | { type: 'ellipsis'; targetPage: number };
export const getPages = ({
  totalPages,
  currentPage,
  siblingCount = 3,
  showEllipsis = true,
}: {
  totalPages: number;
  currentPage: number;
  siblingCount?: number;
  showEllipsis?: boolean;
}): PageItem[] => {
  if (totalPages <= 10) {
    return Array.from({ length: totalPages }, (_, i) => ({
      type: 'page' as const,
      page: i + 1,
    }));
  }

  const pages: PageItem[] = [];
  const left = Math.max(2, currentPage - siblingCount);
  const right = Math.min(totalPages - 1, currentPage + siblingCount);

  // always add first page
  pages.push({ type: 'page', page: 1 });

  if (showEllipsis && left > 2) {
    pages.push({
      type: 'ellipsis',
      targetPage: Math.max(1, currentPage - (siblingCount + 1)),
    });
  }

  for (let i = left; i <= right; i++) {
    pages.push({ type: 'page', page: i });
  }

  if (showEllipsis && right < totalPages - 1) {
    pages.push({
      type: 'ellipsis',
      targetPage: Math.min(totalPages, currentPage + (siblingCount + 1)),
    });
  }

  // always add last page
  pages.push({ type: 'page', page: totalPages });

  return pages;
};

export type PaginationProps = {
  totalPages: number;
  currentPage: number;
  onPageChange: (page: number) => void;
} & ComponentPropsWithoutRef<'div'>;

export const Pagination = forwardRef<HTMLDivElement, PaginationProps>(
  ({ totalPages, currentPage, onPageChange, className, ...props }, ref) => {
    if (totalPages <= 1) return null;

    const pages = getPages({ totalPages, currentPage });

    return (
      <div ref={ref} className={cn('flex items-center', className)} {...props}>
        <Button
          iconed
          variant="text"
          leftIconName="chevron-left"
          disabled={currentPage === 1}
          onClick={() => onPageChange(currentPage - 1)}
        />

        {pages.map((item, idx) =>
          item.type === 'ellipsis' ? (
            <button
              className="px-2 text-gray-500"
              key={`ellipsis-${idx}`}
              onClick={() => onPageChange(item.targetPage)}
            >
              ...
            </button>
          ) : (
            <button
              key={item.page}
              className={cn(
                'hover:text-primary-5 dark:hover:text-primary-text rounded-xs px-2 leading-[22px]',
                item.page === currentPage &&
                  'border-primary-text text-primary-text border'
              )}
              onClick={() => onPageChange(item.page)}
            >
              {item.page}
            </button>
          )
        )}

        <Button
          iconed
          variant="text"
          rightIconName="chevron-right"
          disabled={currentPage === totalPages}
          onClick={() => onPageChange(currentPage + 1)}
        />
      </div>
    );
  }
);

Pagination.displayName = 'Pagination';
